/**
 * subjectVisuals.js — Single source of truth for subject icon + color mapping.
 *
 * Backed by the 44-subject MASTER_SUBJECT_LIST defined in
 * `backend/routers/chat_helpers.py`. Every subject the classifier can return
 * has an explicit (icon, color) mapping here. A graceful default falls back
 * to BookOpen + warm indigo so future taxonomy additions don't visually
 * break.
 *
 * Legacy aliases let pre-overhaul data (e.g. conv.subject === "Math") still
 * render correctly until the backfill propagates new names.
 */
import {
  // Languages
  Languages, BookOpen, Scroll, Globe2,
  // Math
  Calculator, Sigma,
  // Sciences
  Beaker, Atom, FlaskConical, Leaf, TreePine, Dna,
  // Social Sciences
  Building2, Landmark, Globe, Vote, Users, Brain, TrendingUp,
  // Business
  Briefcase, Receipt, Rocket,
  // Tech
  Cpu, Code2, Database, Bot, AppWindow, Smartphone, MonitorSmartphone,
  // Professional
  Scale, Home, Newspaper,
  // Arts
  Palette, Music, Sparkles, Shirt, PenTool, Hammer, MapPinned,
  // Standalones
  Activity, HeartPulse, Lightbulb,
} from 'lucide-react';

/**
 * Map from subject name → { icon, bg, text, ring }.
 *
 * - bg + text are the pill background + text classes (Tailwind)
 * - ring is the accent ring/border class (used for icon container)
 */
const SUBJECT_VISUALS = {
  // ────────── Languages ──────────
  'English': { icon: BookOpen, bg: 'bg-purple-100', text: 'text-purple-700', ring: 'ring-purple-200' },
  'Hindi': { icon: Languages, bg: 'bg-rose-100', text: 'text-rose-700', ring: 'ring-rose-200' },
  'Sanskrit': { icon: Scroll, bg: 'bg-amber-100', text: 'text-amber-800', ring: 'ring-amber-200' },
  'Regional Language': { icon: Languages, bg: 'bg-fuchsia-100', text: 'text-fuchsia-700', ring: 'ring-fuchsia-200' },
  'Foreign Language': { icon: Globe2, bg: 'bg-sky-100', text: 'text-sky-700', ring: 'ring-sky-200' },

  // ────────── Mathematics ──────────
  'Foundational Numeracy & Mathematics': { icon: Calculator, bg: 'bg-green-100', text: 'text-green-700', ring: 'ring-green-200' },
  'Mathematics': { icon: Calculator, bg: 'bg-green-100', text: 'text-green-700', ring: 'ring-green-200' },
  'Applied Mathematics': { icon: Sigma, bg: 'bg-lime-100', text: 'text-lime-700', ring: 'ring-lime-200' },

  // ────────── Sciences ──────────
  'Environmental Studies': { icon: TreePine, bg: 'bg-emerald-100', text: 'text-emerald-700', ring: 'ring-emerald-200' },
  'General Science': { icon: Beaker, bg: 'bg-blue-100', text: 'text-blue-700', ring: 'ring-blue-200' },
  'Physics': { icon: Atom, bg: 'bg-indigo-100', text: 'text-indigo-700', ring: 'ring-indigo-200' },
  'Chemistry': { icon: FlaskConical, bg: 'bg-teal-100', text: 'text-teal-700', ring: 'ring-teal-200' },
  'Biology': { icon: Leaf, bg: 'bg-emerald-100', text: 'text-emerald-700', ring: 'ring-emerald-200' },
  'Biotechnology': { icon: Dna, bg: 'bg-cyan-100', text: 'text-cyan-700', ring: 'ring-cyan-200' },

  // ────────── Social Sciences & Humanities ──────────
  'Social Science': { icon: Building2, bg: 'bg-amber-100', text: 'text-amber-800', ring: 'ring-amber-200' },
  'History': { icon: Landmark, bg: 'bg-amber-100', text: 'text-amber-800', ring: 'ring-amber-200' },
  'Geography': { icon: Globe, bg: 'bg-emerald-100', text: 'text-emerald-700', ring: 'ring-emerald-200' },
  'Political Science': { icon: Vote, bg: 'bg-red-100', text: 'text-red-700', ring: 'ring-red-200' },
  'Sociology': { icon: Users, bg: 'bg-violet-100', text: 'text-violet-700', ring: 'ring-violet-200' },
  'Psychology': { icon: Brain, bg: 'bg-pink-100', text: 'text-pink-700', ring: 'ring-pink-200' },
  'Economics': { icon: TrendingUp, bg: 'bg-orange-100', text: 'text-orange-700', ring: 'ring-orange-200' },

  // ────────── Business & Commerce ──────────
  'Accountancy': { icon: Receipt, bg: 'bg-slate-100', text: 'text-slate-700', ring: 'ring-slate-200' },
  'Business Studies': { icon: Briefcase, bg: 'bg-stone-100', text: 'text-stone-700', ring: 'ring-stone-200' },
  'Entrepreneurship': { icon: Rocket, bg: 'bg-orange-100', text: 'text-orange-700', ring: 'ring-orange-200' },

  // ────────── Technology & Computer Sciences ──────────
  'Computer Applications': { icon: MonitorSmartphone, bg: 'bg-cyan-100', text: 'text-cyan-700', ring: 'ring-cyan-200' },
  'Information Technology': { icon: Cpu, bg: 'bg-cyan-100', text: 'text-cyan-700', ring: 'ring-cyan-200' },
  'Informatics Practices': { icon: Cpu, bg: 'bg-cyan-100', text: 'text-cyan-700', ring: 'ring-cyan-200' },
  'Computer Science': { icon: Code2, bg: 'bg-blue-100', text: 'text-blue-700', ring: 'ring-blue-200' },
  'Artificial Intelligence': { icon: Bot, bg: 'bg-violet-100', text: 'text-violet-700', ring: 'ring-violet-200' },
  'Data Science': { icon: Database, bg: 'bg-indigo-100', text: 'text-indigo-700', ring: 'ring-indigo-200' },
  'Web Application': { icon: AppWindow, bg: 'bg-sky-100', text: 'text-sky-700', ring: 'ring-sky-200' },

  // ────────── Professional & Applied Sciences ──────────
  'Legal Studies': { icon: Scale, bg: 'bg-slate-100', text: 'text-slate-700', ring: 'ring-slate-200' },
  'Home Science': { icon: Home, bg: 'bg-yellow-100', text: 'text-yellow-700', ring: 'ring-yellow-200' },
  'Mass Media Studies': { icon: Newspaper, bg: 'bg-rose-100', text: 'text-rose-700', ring: 'ring-rose-200' },

  // ────────── Creative, Performing & Vocational Arts ──────────
  'Art Education': { icon: Palette, bg: 'bg-pink-100', text: 'text-pink-700', ring: 'ring-pink-200' },
  'Music': { icon: Music, bg: 'bg-fuchsia-100', text: 'text-fuchsia-700', ring: 'ring-fuchsia-200' },
  'Dance': { icon: Sparkles, bg: 'bg-rose-100', text: 'text-rose-700', ring: 'ring-rose-200' },
  'Fashion Studies': { icon: Shirt, bg: 'bg-pink-100', text: 'text-pink-700', ring: 'ring-pink-200' },
  'Design': { icon: PenTool, bg: 'bg-violet-100', text: 'text-violet-700', ring: 'ring-violet-200' },
  'Crafts & Vocational Skills': { icon: Hammer, bg: 'bg-amber-100', text: 'text-amber-800', ring: 'ring-amber-200' },
  'Tourism': { icon: MapPinned, bg: 'bg-teal-100', text: 'text-teal-700', ring: 'ring-teal-200' },

  // ────────── Standalones ──────────
  'Physical Education': { icon: Activity, bg: 'bg-red-100', text: 'text-red-700', ring: 'ring-red-200' },
  'Yoga': { icon: HeartPulse, bg: 'bg-rose-100', text: 'text-rose-700', ring: 'ring-rose-200' },
  'General Knowledge': { icon: Lightbulb, bg: 'bg-orange-100', text: 'text-orange-700', ring: 'ring-orange-200' },
};

/**
 * Legacy aliases — keep pre-overhaul data rendering correctly.
 *
 * The classifier previously used short keys ("Math", "Science", "Arts",
 * "Technology") that the new master list either renamed or replaced. Until
 * those rows are reclassified or backfilled, alias them to the new visual.
 */
const SUBJECT_ALIASES = {
  'Math': 'Mathematics',
  'Maths': 'Mathematics',
  'Science': 'General Science',
  'Arts': 'Art Education',
  'Art': 'Art Education',
  'Technology': 'Information Technology',
  'Civics': 'Political Science',
  // Common LLM drift / paraphrases
  'Social Studies': 'Social Science',
  'Social Sciences': 'Social Science',
  'CS': 'Computer Science',
  'AI': 'Artificial Intelligence',
  'PE': 'Physical Education',
  'GK': 'General Knowledge',
};

const DEFAULT_VISUAL = {
  icon: BookOpen,
  bg: 'bg-indigo-100',
  text: 'text-indigo-700',
  ring: 'ring-indigo-200',
};

/**
 * Resolve the canonical subject name (applies aliases).
 */
export function resolveSubjectName(rawSubject) {
  if (!rawSubject || typeof rawSubject !== 'string') return null;
  const trimmed = rawSubject.trim();
  if (!trimmed) return null;
  if (SUBJECT_VISUALS[trimmed]) return trimmed;
  if (SUBJECT_ALIASES[trimmed]) return SUBJECT_ALIASES[trimmed];
  // Case-insensitive last-resort match
  const lower = trimmed.toLowerCase();
  for (const key of Object.keys(SUBJECT_VISUALS)) {
    if (key.toLowerCase() === lower) return key;
  }
  for (const [alias, canonical] of Object.entries(SUBJECT_ALIASES)) {
    if (alias.toLowerCase() === lower) return canonical;
  }
  return trimmed; // unknown — caller gets default visual
}

/**
 * Get the full visual descriptor for a subject. Returns DEFAULT_VISUAL if
 * the subject is unknown or null.
 */
export function getSubjectVisual(rawSubject) {
  const name = resolveSubjectName(rawSubject);
  if (!name) return DEFAULT_VISUAL;
  return SUBJECT_VISUALS[name] || DEFAULT_VISUAL;
}

/**
 * Convenience: just the icon component.
 */
export function getSubjectIcon(rawSubject) {
  return getSubjectVisual(rawSubject).icon;
}

/**
 * Convenience: combined Tailwind class string for a pill background + text.
 */
export function getSubjectPillClass(rawSubject) {
  const v = getSubjectVisual(rawSubject);
  return `${v.bg} ${v.text}`;
}

export { SUBJECT_VISUALS, DEFAULT_VISUAL };
